import React, { useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { components } from "react-select";
import { connect } from "react-redux";
import { Field, formValueSelector, reduxForm } from "redux-form";

import { customInput } from "../../../../../../common/custom-input";
import { customTextarea } from "../../../../../../common/customTextarea";
import { CustomCheckbox } from "../../../../../../common/customCheckbox";
import { CustomSingleSelect } from "../../../../../../common/new-core-ui/CustomSingleSelect";
import v2FormSelectStyle from "../../../../../v2FormSelectStyle";
import styles from "../../../../../SwmsMasters/SwmsMastersStyle.module.css";
import {
  ANSWER_SCOPE_OPTIONS_V3,
  FLAG_NONE_OPTION_V3,
  RESPONSE_TYPE_OPTIONS_V3,
  getFlagOptionV3,
  validatePreStartQuestionV3
} from "./preStartQuestionFormConfigV3";
import { getFlagOptionsV3, sanitizeResponseConfigForTypeV3 } from "./preStartQuestionResponseConfigUtilsV3";

let JobSwmsV3AddEditPreStartQuestionForm = ({
  mode,
  selectedQuestion,
  methodOptions,
  handleSubmit,
  pristine,
  selectedActive,
  selectedResponseType,
  selectedFlagOption,
  responseConfig,
  onClose,
  onSubmitSuccess,
  change
}) => {
  const [submitting, setSubmitting] = useState(false);
  const responseTypeValue = selectedResponseType && selectedResponseType.value ? selectedResponseType.value : "";
  const normalizedResponseConfig = sanitizeResponseConfigForTypeV3(responseTypeValue, responseConfig || {});
  const showFlagField = normalizedResponseConfig.type === "radio";
  const showUnitField = normalizedResponseConfig.type === "number";

  const flagOptions = useMemo(() => {
    if (!responseTypeValue) {
      return [FLAG_NONE_OPTION_V3];
    }

    return [FLAG_NONE_OPTION_V3, ...getFlagOptionsV3(responseTypeValue)];
  }, [responseTypeValue]);

  const onResponseTypeChange = (responseTypeOption) => {
    const nextResponseTypeValue = responseTypeOption && responseTypeOption.value ? responseTypeOption.value : "";
    const nextConfig = nextResponseTypeValue ? sanitizeResponseConfigForTypeV3(nextResponseTypeValue, responseConfig || {}) : {};

    change("response_type", responseTypeOption);
    change("response_config", nextConfig);
    change("flag_on_value", getFlagOptionV3(nextResponseTypeValue, nextConfig.flagOnValue));
    change("unit", nextConfig.type === "number" && nextConfig.unit != null ? `${nextConfig.unit}` : "");
  };

  const onFlagChange = (flagOption) => {
    const nextFlagValue = flagOption && flagOption.value ? flagOption.value : null;
    change("flag_on_value", flagOption || FLAG_NONE_OPTION_V3);
    change("response_config", {
      ...normalizedResponseConfig,
      flagOnValue: nextFlagValue
    });
  };

  const onUnitChange = (event) => {
    const unitValue = event && event.target ? event.target.value : "";
    change("unit", unitValue);
    change("response_config", {
      ...normalizedResponseConfig,
      unit: unitValue
    });
  };

  const onSubmit = (values) => {
    setSubmitting(true);

    try {
      const selectedMethod = Array.isArray(methodOptions)
        ? methodOptions.find((option) => values.method && `${option.value}` === `${values.method.value}`)
        : null;

      const responseType = values.response_type && values.response_type.value ? values.response_type.value : "";
      const responseConfigFromType = sanitizeResponseConfigForTypeV3(responseType, values.response_config || {});
      const flagValue = values.flag_on_value && values.flag_on_value.value ? values.flag_on_value.value : null;

      const finalizedResponseConfig = (() => {
        if (responseConfigFromType.type === "radio") {
          return {
            ...responseConfigFromType,
            flagOnValue: flagValue
          };
        }

        if (responseConfigFromType.type === "number") {
          return {
            ...responseConfigFromType,
            unit: typeof values.unit === "string" ? values.unit : null
          };
        }

        return responseConfigFromType;
      })();

      onSubmitSuccess({
        mode,
        selectedQuestion,
        question: {
          id: mode === "edit" && selectedQuestion ? selectedQuestion.id : Date.now(),
          question_text: values.question_text ? values.question_text.trim() : "",
          guidance_text: values.guidance_text || "",
          answer_scope: values.answer_scope && values.answer_scope.value ? values.answer_scope.value : "",
          required: values.required ? 1 : 0,
          active: values.active ? 1 : 0,
          response_config: finalizedResponseConfig,
          method_id: selectedMethod ? Number(selectedMethod.value) : 0,
          method_name: selectedMethod ? selectedMethod.label : "Job Specific Question"
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="v2-modal-container">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className={styles.ModalHeading}>
                {mode === "edit" ? "Edit Pre-Start Question" : "Add Pre-Start Question"}
              </div>
              <hr />
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-12">
              <div className={styles.Label}>Method</div>
              <Field
                name="method"
                component={CustomSingleSelect}
                options={methodOptions || []}
                styles={v2FormSelectStyle}
                otherProps={{
                  isDisabled: submitting,
                  hideSelectedOptions: false,
                  components: { dropdownIndicator: <components.DownChevron /> },
                  noOptionsMessage: () => "No Options Available."
                }}
              />
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-12">
              <div className={styles.Label}>Question Text</div>
              <Field
                name="question_text"
                component={customTextarea}
                rows={3}
                disabled={submitting}
                maxLength={500}
                className={styles.TextArea}
                otherProps={{ style: { resize: "vertical" } }}
              />
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <div className={styles.Label}>Response Type</div>
              <Field
                name="response_type"
                component={CustomSingleSelect}
                options={RESPONSE_TYPE_OPTIONS_V3}
                styles={v2FormSelectStyle}
                onChange={onResponseTypeChange}
                otherProps={{
                  isDisabled: submitting,
                  hideSelectedOptions: false,
                  components: { dropdownIndicator: <components.DownChevron /> },
                  noOptionsMessage: () => "No Options Available."
                }}
              />
            </div>
            {showFlagField ? (
              <div className="col-6">
                <div className={styles.Label}>Flag / Non-Conformance On</div>
                <Field
                  name="flag_on_value"
                  component={CustomSingleSelect}
                  options={flagOptions}
                  styles={v2FormSelectStyle}
                  value={selectedFlagOption || FLAG_NONE_OPTION_V3}
                  onChange={onFlagChange}
                  otherProps={{
                    isDisabled: submitting,
                    hideSelectedOptions: false,
                    components: { dropdownIndicator: <components.DownChevron /> },
                    noOptionsMessage: () => "No Options Available."
                  }}
                />
              </div>
            ) : null}

            {showUnitField ? (
              <div className="col-6">
                <div className={styles.Label}>Unit</div>
                <Field
                  name="unit"
                  component={customInput}
                  disabled={submitting}
                  autoComplete="off"
                  className={styles.TextField}
                  maxLength={80}
                  onChange={onUnitChange}
                />
              </div>
            ) : null}
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <div className={styles.Label}>Answer Scope</div>
              <Field
                name="answer_scope"
                component={CustomSingleSelect}
                options={ANSWER_SCOPE_OPTIONS_V3}
                styles={v2FormSelectStyle}
                otherProps={{
                  isDisabled: submitting,
                  hideSelectedOptions: false,
                  components: { dropdownIndicator: <components.DownChevron /> },
                  noOptionsMessage: () => "No Options Available."
                }}
              />
            </div>
            <div className="col-6">
              <div className={styles.Label}>Required</div>
              <Field name="required" component={CustomCheckbox} disabled={submitting} />
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-12">
              <div className={styles.Label}>Guidance Note</div>
              <Field
                name="guidance_text"
                component={customInput}
                disabled={submitting}
                autoComplete="off"
                className={styles.TextField}
                maxLength={240}
              />
            </div>
          </div>

          <div className="row mb-2 pt-2">
            <div className="col-6">
              <div className={styles.Label}>Enabled</div>
              <Field name="active" component={CustomCheckbox} disabled={submitting} />
            </div>
          </div>

          {!selectedActive ? (
            <div className={styles.WarningAlertBox} role="alert">
              <div className={styles.WarningAlertText}>
                This question will remain visible in the SWMS Form, cannot be dragged while inactive, and will not be persisted until the Pre-Start Checklist is saved.
              </div>
            </div>
          ) : null}

          <div className="row">
            <div className="col-12 pt-3">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "end", columnGap: "10px" }}>
                {submitting ? <CircularProgress size={20} /> : ""}
                <button
                  type="button"
                  className={styles.OutlinedButton}
                  onClick={onClose}
                  style={{ padding: "0px 5px" }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.PrimaryButton}
                  disabled={submitting || pristine}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

const selector = formValueSelector("JobSwmsV3AddEditPreStartQuestionForm");

JobSwmsV3AddEditPreStartQuestionForm = reduxForm({
  form: "JobSwmsV3AddEditPreStartQuestionForm",
  destroyOnUnmount: true,
  enableReinitialize: true,
  validate: validatePreStartQuestionV3
})(JobSwmsV3AddEditPreStartQuestionForm);

JobSwmsV3AddEditPreStartQuestionForm = connect((state) => ({
  selectedResponseType: selector(state, "response_type"),
  selectedFlagOption: selector(state, "flag_on_value"),
  responseConfig: selector(state, "response_config"),
  selectedActive: selector(state, "active")
}))(JobSwmsV3AddEditPreStartQuestionForm);

export default JobSwmsV3AddEditPreStartQuestionForm;
